import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
	ICampaign,
	ICampaignMetrics,
	ICampaignFormData,
	CampaignStatus,
	ICampaignResults,
	QuestionType,
	IFeedbackWinner,
	IFeedbackSubmission,
	OwnershipType,
} from '../interface/ICampaign';
import { IGuestConnectSettings } from '../interface/IGuestConnectSettings';
import CampaignMetrics from '../components/CampaignMetrics/CampaignMetrics';
import CampaignForm from '../components/CampaignForm/CampaignForm';
import CampaignResults from '../components/CampaignResults/CampaignResults';
import QRCodeGenerator from '../components/QRCodeGenerator/QRCodeGenerator';
import {
	CreateCampaign,
	UpdateCampaign,
	GetCampaigns,
	GetCampaignSubmissions,
	GetGuestSentimentAnalyses,
	GetFeedbackWinners,
	PickCampaignWinners,
} from 'core/API/workplaces';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import { IGuestSentiment } from '../interface/IGuestSentiment';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Tooltip,
	Typography,
	Box,
	Card,
	CardContent,
	Grid,
	Chip,
	Divider,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import {
	Visibility as VisibilityIcon,
	QrCode2 as QrCodeIcon,
	Edit as EditIcon,
} from '@mui/icons-material';
import style from './CampaignDashboard.module.scss';
import { Button } from '../../../components/ui/button';

interface CampaignDashboardProps {
	// These would come from API/state management
	campaigns?: ICampaign[];
	metrics?: ICampaignMetrics;
	_availableRewards?: Array<{ id: string; name: string }>;
	organizationId: string;
	settings?: IGuestConnectSettings;
}

export default function CampaignDashboard({
	campaigns: initialCampaigns = [],
	metrics: initialMetrics,
	_availableRewards = [],
	organizationId,
	settings: _settings,
}: CampaignDashboardProps): JSX.Element {
	// Use env variable for GuestConnect base URL
	const guestConnectBaseUrl = process.env.REACT_APP_GUESTCONNECT_BASE_URL || window.location.origin;
	// Get current workplace ID from context
	const { workplaceId } = useWorkplacesContext();

	// Permission checks for Guest Connect module
	const { hasModuleViewPermission, hasModuleEditPermission, hasModuleDeletePermission, hasModuleExecutePermission } = usePermissionCheck();
	const _canViewGuestConnect = hasModuleViewPermission('Guest Connect');
	const canEditGuestConnect = hasModuleEditPermission('Guest Connect');
	const _canDeleteGuestConnect = hasModuleDeletePermission('Guest Connect');
	const canExecuteGuestConnect = hasModuleExecutePermission('Guest Connect');

	// Guest sentiment state
	const [guestSentiment, setGuestSentiment] = useState<IGuestSentiment | null>(null);

	// Settings can be used to configure campaign behavior:
	// - allowMultipleSubmissionsPerDay: Control if users can submit multiple times
	// - disclaimerText: Display in campaign response form
	// - logoPath: Show organization logo in campaigns

	const [campaigns, setCampaigns] = useState<ICampaign[]>(initialCampaigns);
	const [showForm, setShowForm] = useState(false);
	const [editingCampaign, setEditingCampaign] = useState<ICampaign | null>(null);
	const [viewingResults, setViewingResults] = useState<ICampaign | null>(null);
	const [campaignResults, setCampaignResults] = useState<ICampaignResults | null>(null);
	const [showQRView, setShowQRView] = useState(false);
	const [selectedCampaignForQR, setSelectedCampaignForQR] = useState<ICampaign | null>(null);
	const [showSentimentView, setShowSentimentView] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingResults, setIsLoadingResults] = useState(false);
	const [campaignStats, setCampaignStats] = useState<
		Map<string, { submissions: number; averageRating: number | null }>
	>(new Map());
	const [_feedbackWinners, setFeedbackWinners] = useState<Map<string, IFeedbackWinner[]>>(
		new Map()
	);
	const [showEditWarning, setShowEditWarning] = useState(false);
	const [pendingFormData, setPendingFormData] = useState<ICampaignFormData | null>(null);
	// Fetch guest sentiment function
	const fetchGuestSentiment = useCallback(async () => {
		try {
			const response = await GetGuestSentimentAnalyses(workplaceId?.toString());
			console.log('Fetched guest sentiment analyses:', response);
			if (response && response.length > 0) {
				// Get the most recent sentiment analysis
				const latestSentiment = response.sort(
					(a: IGuestSentiment, b: IGuestSentiment) =>
						new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
				)[0];
				setGuestSentiment(latestSentiment);
			}
		} catch (error) {
			console.error('Error fetching guest sentiment:', error);
		}
	}, [workplaceId]);

	// Fetch campaign statistics (submissions and ratings)
	const fetchCampaignStats = useCallback(
		async (campaigns: ICampaign[]) => {
			try {
				// paginationService automatically handles fetching all pages
				const allSubmissions = await GetCampaignSubmissions(workplaceId?.toString() || '');
				const statsMap = new Map<string, { submissions: number; averageRating: number | null }>();

				campaigns.forEach((campaign) => {
					const campaignIRI = `/api/guest_connect_feedback_campaigns/${campaign.id}`;
					const submissions = allSubmissions.filter(
						(submission: IFeedbackSubmission) => submission.campaign?.['@id'] === campaignIRI
					);

					// Calculate average rating for this campaign
					let averageRating: number | null = null;
					const ratingAnswers: number[] = [];

					submissions.forEach((submission: IFeedbackSubmission) => {
						submission.answers?.forEach((answer) => {
							if (answer.question?.questionType === QuestionType.RATING) {
								const rating = parseInt(answer.value);
								if (!isNaN(rating) && rating >= 1 && rating <= 5) {
									ratingAnswers.push(rating);
								}
							}
						});
					});

					if (ratingAnswers.length > 0) {
						averageRating = ratingAnswers.reduce((sum, val) => sum + val, 0) / ratingAnswers.length;
					}

					statsMap.set(campaign.id, {
						submissions: submissions.length,
						averageRating,
					});
				});

				setCampaignStats(statsMap);
			} catch (error) {
				console.error('Error fetching campaign stats:', error);
			}
		},
		[workplaceId, setCampaignStats]
	);

	// Fetch feedback winners
	const fetchFeedbackWinners = useCallback(async () => {
		try {
			const winners = await GetFeedbackWinners(workplaceId?.toString());
			const winnersMap = new Map<string, IFeedbackWinner[]>();

			winners.forEach((winner: IFeedbackWinner) => {
				const campaignId = winner.campaign.split('/').pop();
				if (campaignId) {
					if (!winnersMap.has(campaignId)) {
						winnersMap.set(campaignId, []);
					}
					winnersMap.get(campaignId)?.push(winner);
				}
			});

			setFeedbackWinners(winnersMap);
		} catch (error) {
			console.error('Error fetching feedback winners:', error);
		}
	}, [workplaceId]);

	// Fetch campaigns function - reusable
	const fetchCampaigns = useCallback(async () => {
		setIsLoading(true);
		try {
			// paginationService automatically handles fetching all pages
			const response = await GetCampaigns(workplaceId?.toString());
			const allCampaigns = response?.member || response?.['hydra:member'] || [];

			// Transform API response to ICampaign format
			const fetchedCampaigns: ICampaign[] = allCampaigns.map((campaign: any) => {
				// Strip time and timezone suffixes, keep only date part (YYYY-MM-DD)
				const startDateStr = campaign.startDate.split('T')[0];
				const endDateStr = campaign.endDate.split('T')[0];

				// Determine status based on dates (comparing dates only, not time)
				const now = new Date();
				const todayStr = now.toISOString().split('T')[0];

				let status: CampaignStatus;
				if (todayStr < startDateStr) {
					// Start date is in the future
					status = CampaignStatus.SCHEDULED;
				} else if (todayStr > endDateStr) {
					// End date is in the past
					status = CampaignStatus.PAST;
				} else {
					// Current date is between start and end (inclusive)
					status = CampaignStatus.ACTIVE;
				}

				return {
					id: campaign.id?.toString() || '',
					title: campaign.title,
					ownershipType: campaign.workplace ? OwnershipType.WORKPLACE : OwnershipType.ORGANIZATION,
					organizationId,
					workplaceId: campaign.workplace?.id?.toString(),
					startDate: startDateStr,
					endDate: endDateStr,
					questions:
						campaign.questions?.map((q: any) => ({
							id: q.id?.toString() || '',
							questionText: q.questionText,
							questionType: q.questionType,
							sortOrder: q.sortOrder,
							createdAt: campaign.startDate,
							updatedAt: campaign.startDate,
						})) || [],
					uniqueUrl: campaign.uniqueUrlToken
						? `${guestConnectBaseUrl}/campaign/${campaign.uniqueUrlToken}`
						: '',
					qrCode: '',
					reward: campaign.reward
						? {
								'@id': campaign.reward['@id']?.toString() || '',
								rewardName: campaign.reward.title || '',
								numberOfWinners: campaign.winnersCount || 0,
								rewardDetailsText: campaign.rewardDetailsText || '',
								enabled: true,
							}
						: undefined,
					status,
					createdAt: campaign.startDate,
					updatedAt: campaign.startDate,
					createdBy: organizationId,
				};
			});

			setCampaigns(fetchedCampaigns);
			// Fetch stats for all campaigns
			await fetchCampaignStats(fetchedCampaigns);
		} catch (error) {
			console.error('Error fetching campaigns:', error);
		} finally {
			setIsLoading(false);
		}
	}, [workplaceId, organizationId, guestConnectBaseUrl, fetchCampaignStats]);

	// Fetch campaigns on component mount
	useEffect(() => {
		fetchCampaigns();
		fetchGuestSentiment();
		fetchFeedbackWinners();
	}, [fetchCampaigns, fetchGuestSentiment, fetchFeedbackWinners]);

	// Categorize campaigns by status and sort by most recent first
	const { activeCampaigns, pastCampaigns, scheduledCampaigns } = useMemo(() => {
		const active: ICampaign[] = [];
		const past: ICampaign[] = [];
		const scheduled: ICampaign[] = [];
		const now = new Date();

		campaigns.forEach((campaign) => {
			// Extract just date parts for comparison (no time)
			const startDateStr = campaign.startDate.split('T')[0];
			const endDateStr = campaign.endDate.split('T')[0];
			const todayStr = now.toISOString().split('T')[0];

			if (todayStr < startDateStr) {
				// Start date is in the future
				scheduled.push(campaign);
			} else if (todayStr > endDateStr) {
				// End date is in the past
				past.push(campaign);
			} else {
				// Current date is between start and end (inclusive)
				active.push(campaign);
			}
		});

		// Sort by start date descending (most recent first)
		const sortByDateDesc = (a: ICampaign, b: ICampaign) =>
			new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

		active.sort(sortByDateDesc);
		past.sort(sortByDateDesc);
		scheduled.sort(sortByDateDesc);

		return { activeCampaigns: active, pastCampaigns: past, scheduledCampaigns: scheduled };
	}, [campaigns]);

	// Calculate metrics
	const metrics: ICampaignMetrics = useMemo(() => {
		if (initialMetrics) return initialMetrics;

		// Calculate overall average rating across all campaigns
		let overallAverageRating = 0;
		const campaignsWithRatings = Array.from(campaignStats.values()).filter(
			(stats) => stats.averageRating !== null
		);

		if (campaignsWithRatings.length > 0) {
			const totalRating = campaignsWithRatings.reduce(
				(sum, stats) => sum + (stats.averageRating || 0),
				0
			);
			overallAverageRating = totalRating / campaignsWithRatings.length;
		}

		return {
			totalCampaigns: campaigns.length,
			activeCampaigns: activeCampaigns.length,
			totalResponses: 0,
			averageResponseRate: 0,
			averageSentimentScore: 0, // Sentiment score is separate from campaign ratings
			averageRating: overallAverageRating, // Average rating from campaigns
		};
	}, [campaigns, activeCampaigns, initialMetrics, campaignStats]);

	const handleCreateCampaign = async (formData: ICampaignFormData) => {
		if (!canExecuteGuestConnect) {
			alert('You do not have permission to create campaigns');
			return;
		}

		try {
			// Parse start and end dates
			const startDate = new Date(formData.startDate);
			const endDate = new Date(formData.endDate);

			// Extract just the date parts (YYYY-MM-DD)
			const startDateOnly = startDate.toISOString().split('T')[0];
			const endDateOnly = endDate.toISOString().split('T')[0];

			// Transform form data to API payload format
			const payload = {
				workplace: formData.workplaceId ? `/api/workplaces/${formData.workplaceId}` : null,
				title: formData.title,
				startDate: startDateOnly,
				endDate: endDateOnly,
				reward: formData.reward?.rewardId ? formData.reward.rewardId : null,
				rewardDetailsText: formData.reward?.rewardDetailsText || '',
				winnersCount: formData.reward?.numberOfWinners || 0,
				active: true,
				questions: formData.questions.map((q) => ({
					questionText: q.questionText,
					questionType: q.questionType,
					sortOrder: q.sortOrder,
				})),
			};

			// Call API to create campaign
			await CreateCampaign(payload);

			// Refresh campaigns list from API
			await fetchCampaigns();

			setShowForm(false);
			setEditingCampaign(null);
		} catch (error) {
			console.error('Error creating campaign:', error);
			alert('Failed to create campaign. Please try again.');
		}
	};

	const handleUpdateCampaign = async (formData: ICampaignFormData) => {
		if (!editingCampaign) return;

		if (!canEditGuestConnect) {
			alert('You do not have permission to edit campaigns');
			return;
		}

		// Check if campaign is active and show warning
		if (editingCampaign.status === CampaignStatus.ACTIVE) {
			setPendingFormData(formData);
			setShowEditWarning(true);
			return;
		}

		// Proceed with update
		await performUpdate(formData);
	};

	const performUpdate = async (formData: ICampaignFormData) => {
		if (!editingCampaign) return;

		try {
			// Parse start and end dates
			const startDate = new Date(formData.startDate);
			const endDate = new Date(formData.endDate);

			// Extract just the date parts (YYYY-MM-DD)
			const startDateOnly = startDate.toISOString().split('T')[0];
			const endDateOnly = endDate.toISOString().split('T')[0];

			// Transform form data to API payload format
			const payload = {
				workplace: formData.workplaceId ? `/api/workplaces/${formData.workplaceId}` : null,
				title: formData.title,
				startDate: startDateOnly,
				endDate: endDateOnly,
				reward: formData.reward?.rewardId ? formData.reward.rewardId : null,
				rewardDetailsText: formData.reward?.rewardDetailsText || '',
				winnersCount: formData.reward?.numberOfWinners || 0,
				active: editingCampaign.status === CampaignStatus.ACTIVE,
				questions: formData.questions.map((q) => ({
					questionText: q.questionText,
					questionType: q.questionType,
					sortOrder: q.sortOrder,
				})),
			};

			// Call API to update campaign
			await UpdateCampaign(editingCampaign.id, payload);

			// Refresh campaigns list from API
			await fetchCampaigns();

			setShowForm(false);
			setEditingCampaign(null);
		} catch (error) {
			console.error('Error updating campaign:', error);
			alert('Failed to update campaign. Please try again.');
		}
	};

	const handleConfirmEdit = async () => {
		setShowEditWarning(false);
		if (pendingFormData) {
			await performUpdate(pendingFormData);
			setPendingFormData(null);
		}
	};

	const handleCancelEdit = () => {
		setShowEditWarning(false);
		setPendingFormData(null);
	};

	const handleEditCampaign = (campaign: ICampaign) => {
		setEditingCampaign(campaign);
		setShowForm(true);
		setViewingResults(null);
	};

	const handleCancelForm = () => {
		setShowForm(false);
		setEditingCampaign(null);
	};

	const handleViewResults = async (campaign: ICampaign) => {
		setViewingResults(campaign);
		setShowForm(false);
		setIsLoadingResults(true);
		setCampaignResults(null);

		try {
			// Fetch all submissions for this workplace (paginationService handles all pages automatically)
			const allSubmissions = await GetCampaignSubmissions(workplaceId?.toString() || '');

			// Filter submissions for this specific campaign by matching campaign['@id']
			const campaignIRI = `/api/guest_connect_feedback_campaigns/${campaign.id}`;
			const submissions = allSubmissions.filter(
				(submission: IFeedbackSubmission) => submission.campaign?.['@id'] === campaignIRI
			);

			// Transform submissions to ICampaignResults
			const questionResults = campaign.questions.map((question) => {
				const questionAnswers = submissions.flatMap((submission: IFeedbackSubmission) =>
					submission.answers
						.filter((answer) => answer.question.id.toString() === question.id)
						.map((answer: any) => answer.value)
				);

				if (question.questionType === QuestionType.YES_NO) {
					const yesCount = questionAnswers.filter(
						(val: any) => val === 'yes' || val === 'true' || val === true
					).length;
					const noCount = questionAnswers.filter(
						(val: any) => val === 'no' || val === 'false' || val === false
					).length;
					const total = yesCount + noCount;

					return {
						questionId: question.id,
						questionText: question.questionText,
						questionType: question.questionType,
						yesCount,
						noCount,
						yesPercentage: total > 0 ? (yesCount / total) * 100 : 0,
						noPercentage: total > 0 ? (noCount / total) * 100 : 0,
						totalAnswers: total,
					};
				} else if (question.questionType === QuestionType.COMMENT) {
					// Comment question
					const comments = questionAnswers.filter((val: any) => val && typeof val === 'string' && val.trim() !== '');

					return {
						questionId: question.id,
						questionText: question.questionText,
						questionType: question.questionType,
						comments,
						totalAnswers: comments.length,
					};
				} else {
					// Rating question
					const ratings = questionAnswers
						.map((val: any) => parseInt(val))
						.filter((val: number) => !isNaN(val) && val >= 1 && val <= 5);

					const ratingDistribution: any = {
						1: 0,
						2: 0,
						3: 0,
						4: 0,
						5: 0,
					};

					ratings.forEach((rating: number) => {
						ratingDistribution[rating] = (ratingDistribution[rating] || 0) + 1;
					});

					const averageRating =
						ratings.length > 0
							? ratings.reduce((sum: number, val: number) => sum + val, 0) / ratings.length
							: 0;

					return {
						questionId: question.id,
						questionText: question.questionText,
						questionType: question.questionType,
						averageRating,
						ratingDistribution,
						totalAnswers: ratings.length,
					};
				}
			});

			// Fetch winners fresh for this campaign to avoid stale state issues
			const allWinners = await GetFeedbackWinners(workplaceId?.toString());
			const currentCampaignWinners = allWinners.filter((winner: IFeedbackWinner) => {
				const campaignId = winner.campaign.split('/').pop();
				return campaignId === campaign.id;
			});

			console.log('feedback winners', currentCampaignWinners, submissions);

			// Get winners for this campaign
			const campaignWinners = currentCampaignWinners.map((winner: IFeedbackWinner) => {
				// Find the matching submission by @id (winner.submission is a string IRI)
				const matchingSubmission = submissions.find(
					(sub: IFeedbackSubmission) => sub['@id'] === winner.submission
				);
				console.log('matching submission', matchingSubmission);

				// Extract name from submission (API returns single 'name' field)
				const fullName = matchingSubmission?.name || '';
				const nameParts = fullName.split(' ');
				const firstName = nameParts[0] || '';
				const lastName = nameParts.slice(1).join(' ') || '';

				return {
					id: winner.id.toString(),
					campaignId: campaign.id,
					responseId: winner.submission,
					firstName: firstName,
					lastName: lastName,
					email: matchingSubmission?.email || '',
					phone: matchingSubmission?.phone || '',
					selectedAt: winner.selectedAt,
				};
			});

			// Count submissions with rewardOptIn enabled
			const rewardOptInCount = submissions.filter(
				(submission: IFeedbackSubmission) => submission.rewardOptIn === true
			).length;

			const results: ICampaignResults = {
				campaign,
				totalResponses: submissions.length,
				questionResults,
				winners: campaignWinners,
				rewardOptInCount,
			};

			setCampaignResults(results);
		} catch (error) {
			console.error('Error fetching campaign results:', error);
			alert('Failed to load campaign results. Please try again.');
		} finally {
			setIsLoadingResults(false);
		}
	};

	const handleShowQR = (campaign: ICampaign) => {
		setSelectedCampaignForQR(campaign);
		setShowQRView(true);
	};

	const handleCloseQR = () => {
		setShowQRView(false);
		setSelectedCampaignForQR(null);
	};

	const handlePickWinners = async (campaignId: string) => {
		try {
			await PickCampaignWinners(campaignId);
			// Refresh winners
			await fetchFeedbackWinners();
			// Refresh campaign results if viewing
			if (viewingResults && viewingResults.id === campaignId) {
				await handleViewResults(viewingResults);
			}
			alert('Winners have been selected successfully!');
		} catch (error) {
			console.error('Error picking winners:', error);
			alert('Failed to pick winners. Please try again.');
		}
	};

	const handleSentimentClick = () => {
		// Show guest sentiment detail view inline
		setShowSentimentView(true);
	};

	const handleCloseSentiment = () => {
		setShowSentimentView(false);
	};

	// Helper function to check if campaign has started
	const _hasStarted = (campaign: ICampaign) => {
		const now = new Date();
		const start = new Date(campaign.startDate);
		return now >= start;
	};

	// Helper function to render campaign table
	const renderCampaignTable = (
		campaigns: ICampaign[],
		title: string,
		showEdit: boolean = true,
		isLoading: boolean = false
	) => {
		return (
			<Box sx={{ mb: 0 }}>
				{isLoading ? (
					<Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
						<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
							<Typography variant="h6" sx={{ fontWeight: 600 }}>
								{title}
							</Typography>
						</Box>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								padding: '3rem',
							}}
						>
							<CircularProgress size={40} color="inherit" />
						</Box>
					</Paper>
				) : campaigns.length === 0 ? (
					<Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
						<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
							<Typography variant="h6" sx={{ fontWeight: 600 }}>
								{title} ({campaigns.length})
							</Typography>
						</Box>
						<Box sx={{ p: 3, textAlign: 'center' }}>
							<Typography sx={{ color: '#fff' }}>No {title.toLowerCase()} yet</Typography>
						</Box>
					</Paper>
				) : (
					<TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: '#fff' }}>
									<TableCell colSpan={7} sx={{ py: 2 }}>
										<Typography variant="h6" sx={{ fontWeight: 600 }}>
											{title} ({campaigns.length})
										</Typography>
									</TableCell>
								</TableRow>
								<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
									<TableCell sx={{ fontWeight: 600 }}>Campaign Name</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Questions</TableCell>{' '}
									<TableCell sx={{ fontWeight: 600 }}>Submissions</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Score</TableCell>{' '}
									<TableCell align="center" sx={{ fontWeight: 600 }}>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{campaigns.map((campaign) => {
									return (
										<TableRow key={campaign.id} hover>
											<TableCell>{campaign.title}</TableCell>
											<TableCell>{new Date(campaign.startDate).toLocaleDateString()}</TableCell>
											<TableCell>{new Date(campaign.endDate).toLocaleDateString()}</TableCell>
											<TableCell>{campaign.questions.length}</TableCell>
											<TableCell>{campaignStats.get(campaign.id)?.submissions ?? 0}</TableCell>
											<TableCell>
												{campaignStats.get(campaign.id)?.averageRating !== null &&
												campaignStats.get(campaign.id)?.averageRating !== undefined ? (
													<span style={{ color: '#446388', fontWeight: 'bold' }}>
														{campaignStats.get(campaign.id)?.averageRating?.toFixed(2)}
													</span>
												) : (
													'-'
												)}
											</TableCell>
											<TableCell align="center">
												<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
													<Tooltip title="View Results">
														<IconButton
															size="small"
															onClick={() => handleViewResults(campaign)}
															sx={{ color: '#446388' }}
														>
															<VisibilityIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="View QR Code">
														<IconButton
															size="small"
															onClick={() => handleShowQR(campaign)}
															sx={{ color: '#446388' }}
														>
															<QrCodeIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													{showEdit && canEditGuestConnect && (
														<Tooltip title="Edit Campaign">
															<IconButton
																size="small"
																onClick={() => handleEditCampaign(campaign)}
																sx={{ color: '#446388' }}
															>
																<EditIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
												</Box>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>
		);
	};

	return (
		<div className={style.DashboardContainer}>
			{!showForm && !viewingResults && !showQRView && !showSentimentView && canExecuteGuestConnect && (
				<div className={style.Header}>
					<button 
						onClick={() => setShowForm(true)} 
						className={style.CreateButton}
					>
						Add
					</button>
				</div>
			)}
			{!showForm && !viewingResults && !showQRView && !showSentimentView && (
				<>
					<CampaignMetrics
						metrics={metrics}
						guestSentiment={guestSentiment}
						onSentimentClick={handleSentimentClick}
						campaigns={campaigns}
						campaignStats={campaignStats}
						isLoading={isLoading}
					/>
					<Divider sx={{ my: 4 }} />
					{renderCampaignTable(activeCampaigns, 'Active Campaigns', true, isLoading)}
					{renderCampaignTable(scheduledCampaigns, 'Scheduled Campaigns', true, isLoading)}
					{renderCampaignTable(pastCampaigns, 'Past Campaigns', false, isLoading)}
				</>
			)}
			{showQRView && selectedCampaignForQR && (
				<div className={style.QRViewContainer}>
					<div className={style.QRHeader}>
						<div className={style.HeaderTop}>
							<button onClick={handleCloseQR} className={style.BackButton}>
								Back
							</button>
							<div className={style.ActionButtons}>
								<button
									onClick={() => {
										const qrElement = document.querySelector(
											'img[alt="Campaign QR Code"]'
										) as HTMLImageElement;
										if (qrElement) {
											const link = document.createElement('a');
											link.href = qrElement.src;
											link.download = `${selectedCampaignForQR.title}-qr-code.png`;
											link.click();
										}
									}}
									className={style.ActionButton}
								>
									Download
								</button>
								<button
									onClick={() => {
										const qrElement = document.querySelector(
											'img[alt="Campaign QR Code"]'
										) as HTMLImageElement;
										if (qrElement) {
											const printWindow = window.open('', '_blank');
											if (printWindow) {
												printWindow.document.write(`
												<html>
													<head>
														<title>Print QR Code</title>
														<style>
															body { text-align: center; padding: 20px; }
															img { max-width: 400px; }
															h2 { margin: 20px 0; }
														</style>
													</head>
													<body>
														<h2>${selectedCampaignForQR.title}</h2>
														<img src="${qrElement.src}" alt="QR Code" />
														<p>${selectedCampaignForQR.uniqueUrl}</p>
													</body>
												</html>
											`);
												printWindow.document.close();
												printWindow.focus();
												setTimeout(() => {
													printWindow.print();
													printWindow.close();
												}, 250);
											}
										}
									}}
									className={style.ActionButton}
								>
									Print
								</button>
								<button
									onClick={() => {
										window.location.reload();
									}}
									className={style.ActionButton}
								>
									Regenerate
								</button>
							</div>
						</div>
						<h2>{selectedCampaignForQR.title} - QR Code</h2>
					</div>
					<QRCodeGenerator
						campaignUrl={selectedCampaignForQR.uniqueUrl}
						campaignTitle={selectedCampaignForQR.title}
						autoGenerate={true}
					/>
				</div>
			)}
			{showSentimentView && (
				<div className={style.QRViewContainer}>
					<div className={style.QRHeader}>
						<div className={style.HeaderTop}>
							<button onClick={handleCloseSentiment} className={style.BackButton}>
								Back
							</button>
						</div>
						<h2>Guest Sentiment Analysis</h2>
					</div>
					{guestSentiment ? (
						<Box sx={{ p: 3 }}>
							{/* Sentiment Score Card */}
							<Card sx={{ mb: 3, boxShadow: 3 }}>
								<CardContent>
									<Grid container spacing={3} alignItems="center">
										<Grid item xs={12} md={4}>
											<Box sx={{ textAlign: 'center' }}>
												<Typography variant="h6" color="text.secondary" gutterBottom>
													Overall Sentiment Score
												</Typography>
												<Typography
													variant="h1"
													sx={{
														fontSize: '4rem',
														fontWeight: 'bold',
														color: '#446388',
													}}
												>
													{guestSentiment.sentimentScore}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													out of 10
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} md={8}>
											<Typography variant="h6" gutterBottom>
												Workplace Information
											</Typography>
											<Typography variant="body1" color="text.secondary" gutterBottom>
												<strong>Location:</strong> {guestSentiment.workplaceName}
											</Typography>
											<Typography variant="body1" color="text.secondary" gutterBottom>
												<strong>Zipcode:</strong> {guestSentiment.zipcode}
											</Typography>
											<Typography variant="body1" color="text.secondary">
												<strong>Analysis Date:</strong>{' '}
												{new Date(guestSentiment.createdAt).toLocaleDateString()} at{' '}
												{new Date(guestSentiment.createdAt).toLocaleTimeString()}
											</Typography>
										</Grid>
									</Grid>
								</CardContent>
							</Card>

							{/* Summary */}
							{guestSentiment.summary && (
								<Card sx={{ mb: 3, boxShadow: 2 }}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Summary
										</Typography>
										<Typography variant="body1" color="text.secondary">
											{guestSentiment.summary}
										</Typography>
									</CardContent>
								</Card>
							)}

							<Grid container spacing={3}>
								{/* Top Likes */}
								<Grid item xs={12} md={6}>
									<Card sx={{ height: '100%', boxShadow: 2 }}>
										<CardContent>
											<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
												<Typography
													variant="subtitle1"
													sx={{
														color: '#4caf50',
														display: 'flex',
														alignItems: 'center',
														fontWeight: 700,
														fontSize: '1.1rem',
													}}
												>
													👍 Top Likes
												</Typography>
											</Box>
											<Divider sx={{ mb: 2 }} />
											{guestSentiment.topLikes && guestSentiment.topLikes.length > 0 ? (
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
													{guestSentiment.topLikes.map((like, index) => (
														<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
															<Chip
																label={`${index + 1}`}
																size="small"
																sx={{
																	mr: 2,
																	bgcolor: '#4caf50',
																	color: 'white',
																	fontWeight: 'bold',
																	minWidth: '32px',
																}}
															/>
															<Typography variant="body2">{like}</Typography>
														</Box>
													))}
												</Box>
											) : (
												<Typography color="text.secondary">No likes data available</Typography>
											)}
										</CardContent>
									</Card>
								</Grid>

								{/* Top Dislikes */}
								<Grid item xs={12} md={6}>
									<Card sx={{ height: '100%', boxShadow: 2 }}>
										<CardContent>
											<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
												<Typography
													variant="subtitle1"
													sx={{
														color: '#f44336',
														display: 'flex',
														alignItems: 'center',
														fontWeight: 700,
														fontSize: '1.1rem',
													}}
												>
													👎 Top Dislikes
												</Typography>
											</Box>
											<Divider sx={{ mb: 2 }} />
											{guestSentiment.topDislikes && guestSentiment.topDislikes.length > 0 ? (
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
													{guestSentiment.topDislikes.map((dislike, index) => (
														<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
															<Chip
																label={`${index + 1}`}
																size="small"
																sx={{
																	mr: 2,
																	bgcolor: '#f44336',
																	color: 'white',
																	fontWeight: 'bold',
																	minWidth: '32px',
																}}
															/>
															<Typography variant="body2">{dislike}</Typography>
														</Box>
													))}
												</Box>
											) : (
												<Typography color="text.secondary">No dislikes data available</Typography>
											)}
										</CardContent>
									</Card>
								</Grid>
							</Grid>
						</Box>
					) : (
						<Box sx={{ p: 3, textAlign: 'center' }}>
							<Typography variant="h6" color="text.secondary">
								No sentiment data available for this workplace.
							</Typography>
						</Box>
					)}
				</div>
			)}
			{showForm && (
				<CampaignForm
					initialData={
						editingCampaign
							? {
									title: editingCampaign.title,
									workplaceId: editingCampaign.workplaceId,
									startDate: editingCampaign.startDate,
									endDate: editingCampaign.endDate,
									questions: editingCampaign.questions.map((q) => ({
										questionText: q.questionText,
										questionType: q.questionType,
										sortOrder: q.sortOrder,
									})),
									reward: editingCampaign.reward
										? {
												'@id': editingCampaign.reward['@id'],
												rewardName: editingCampaign.reward.rewardName || '',
												numberOfWinners: editingCampaign.reward.numberOfWinners || 1,
												rewardDetailsText:
													(editingCampaign.reward as any).description ||
													editingCampaign.reward.rewardDetailsText ||
													'',
												enabled: true,
											}
										: undefined,
								}
							: undefined
					}
					onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
					onCancel={handleCancelForm}
					workplaceId={workplaceId?.toString()}
				/>
			)}
			{viewingResults && (
				<CampaignResults
					results={campaignResults || undefined}
					onClose={() => {
						setViewingResults(null);
						setCampaignResults(null);
					}}
					onPickWinners={() => handlePickWinners(viewingResults.id)}
					isLoading={isLoadingResults}
				/>
			)}

			{/* Warning Dialog for editing active campaigns */}
			<Dialog open={showEditWarning} onClose={handleCancelEdit}>
				<DialogTitle>Are You Sure?</DialogTitle>
				<DialogContent>
					<Typography>
						Editing a Feedback Campaign in progress may skew the over all results!
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelEdit} className={style.mabelButton}>
						Cancel
					</Button>
					<Button onClick={handleConfirmEdit} className={style.mabelButton}>
						Continue
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
